// Rules the Views screen shares with backend/services/viewConfig.js, so what
// the screen suggests or accepts is what the server accepts.

// Node labels and relationship types (backend/utils/cypherNames.js IDENTIFIER).
export const IDENTIFIER = /^[A-Za-z][A-Za-z0-9_]{0,59}$/;

// Labels Business Flows uses itself: the explorer's start node, diagram nodes
// (backend/config/diagramFormats.js) and its own configuration nodes.
export const RESERVED_NODE_LABELS = Object.freeze([
  "Root",
  "BPMN",
  "SVG",
  "PNG",
  "Diagram",
  "ViewType",
  "ViewRelationship",
  "ViewField",
  "ViewSetting",
  "ViewConfigLock",
  "PanelType",
  "PanelAttribute",
  "SearchType",
  "ExplorerStart",
  "ContentArchiveEntry",
]);

// Relationships that link diagrams to what they show.
export const RESERVED_RELATIONSHIPS = Object.freeze(["BPMN_OF", "SVG_OF", "DIAGRAM_OF"]);

// Attributes hidden whatever the admin's list says: the id, whether an object
// has parts (computed, never an attribute) and the diagram contents
// (viewConfig.js ALWAYS_IGNORED_ATTRIBUTES; the server's list wins).
export const ALWAYS_KEPT_ATTRIBUTES = Object.freeze(["GUID", "hasAssignment", "BPMN", "SVG", "PNG", "PDF", "Mermaid", "content"]);

// Added to every confirmation of a saved change.
export const USERS_SEE_IT = "Users see it within half a minute.";
export const SAVED = `Saved. ${USERS_SEE_IT}`;

const isReserved = (list, value) => list.some((name) => name.toLowerCase() === String(value).toLowerCase());

// Why a relationship name would be refused, or "" when it is fine.
export const relationshipProblem = (value) => {
  if (!value) return "";
  if (!IDENTIFIER.test(value)) return "Letters, digits and underscores, starting with a letter.";
  if (isReserved(RESERVED_RELATIONSHIPS, value)) return `${value} links diagrams to what they show. Choose one of the type's own.`;
  return "";
};

// Why a database label would be refused, or "" when it is fine.
export const nodeLabelProblem = (value) => {
  if (!value) return "";
  if (!IDENTIFIER.test(value)) return "Letters, digits and underscores, starting with a letter.";
  if (isReserved(RESERVED_NODE_LABELS, value)) return `${value} is used by Business Flows itself. Choose a label of the type's own.`;
  return "";
};

// RISK_OF for "Risk"; always a name the server accepts.
export const suggestRelationship = (name) => {
  let base = String(name || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!base) return "";
  if (!/^[A-Z]/.test(base)) base = `T_${base}`;
  base = base.slice(0, 57).replace(/_+$/, "");
  let suggestion = `${base}_OF`;
  if (relationshipProblem(suggestion)) suggestion = `${base.slice(0, 52).replace(/_+$/, "")}_TYPE_OF`;
  return relationshipProblem(suggestion) ? "" : suggestion;
};

// Risk for "Risk"; always a label the server accepts.
export const suggestNodeLabel = (name) => {
  let base = String(name || "").replace(/[^A-Za-z0-9]/g, "");
  if (!base) return "";
  if (!/^[A-Za-z]/.test(base)) base = `T${base}`;
  base = base.slice(0, 60);
  if (nodeLabelProblem(base)) base = `${base.slice(0, 56)}Type`;
  return nodeLabelProblem(base) ? "" : base;
};

// Names for the palette colours (backend PALETTE), for screen readers.
export const PALETTE_NAMES = Object.freeze({
  "#1a1a2e": "Navy",
  "#2f5bd3": "Blue",
  "#0f7b43": "Green",
  "#8f8a1d": "Olive",
  "#a8431f": "Rust",
  "#5b3fa3": "Purple",
  "#0e7490": "Teal",
  "#9d2a5c": "Plum",
  "#b45309": "Amber",
  "#4d7c0f": "Moss",
  "#be123c": "Crimson",
  "#475569": "Slate",
});

// A palette entry is a hex string, or { value, name } when the server names it.
export const swatchOf = (entry) => {
  if (entry && typeof entry === "object") return { value: entry.value, name: entry.name || PALETTE_NAMES[String(entry.value).toLowerCase()] || "" };
  return { value: entry, name: PALETTE_NAMES[String(entry).toLowerCase()] || "" };
};

export const colourName = (value, palette = []) => {
  const found = palette.map(swatchOf).find((swatch) => String(swatch.value).toLowerCase() === String(value).toLowerCase());
  return found?.name || PALETTE_NAMES[String(value).toLowerCase()] || value || "none";
};
